import React from 'react';
import classnames from 'classnames';
import { Icon, IconProps } from '../icon';
import { isTextLinkProps } from '../../utils/is-link-props';

import styles from './link.module.scss';

const textLinkClass = 'text-link';
const iconLinkClass = 'icon-link';

type Size = 'medium' | 'small';

type HTMLAnchorDefaultProps = { href: string } & Pick<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  'target' | 'rel' | 'onClick' | 'className' | 'title'
>;

export type TextLinkProps = {
  size?: Size;
  variant:
    | 'hyperlink-100'
    | 'hyperlink-200'
    | 'button-primary'
    | 'button-secondary'
    | 'button-ghost'
    | 'hyperlink-standalone'
    | 'hyperlink-legal';
  children: React.ReactNode;
  icon?: IconProps;
  iconPosition?: 'left' | 'right';
  isExternal?: boolean;
  'aria-label'?: string;
} & HTMLAnchorDefaultProps;

export type IconLinkProps = {
  size?: Size;
  variant: 'hyperlink' | 'button-primary' | 'button-secondary' | 'button-ghost';
  icon: IconProps;
  'aria-label': string;
} & HTMLAnchorDefaultProps;

export type LinkProps = TextLinkProps | IconLinkProps;

export const TextLink: React.FC<TextLinkProps> = ({
  href,
  target,
  rel,
  onClick,
  title,
  children,
  className,
  icon,
  variant,
  size,
  isExternal,
  iconPosition = 'left',
  'aria-label': ariaLabel
}) => {
  const modifiedIconPosition = isExternal ? 'right' : iconPosition;

  const chooseIcon = () => {
    if (isExternal) {
      return <Icon {...icon} name="external-link--small" className={styles[`${textLinkClass}__icon`]} aria-hidden />;
    } else if (icon) {
      return <Icon {...icon} className={styles[`${textLinkClass}__icon`]} aria-hidden />;
    }
  };

  const classes = classnames(
    styles[textLinkClass],
    { [styles[`${textLinkClass}--${variant}`]]: variant },
    { [styles[`${textLinkClass}--button`]]: ['button-primary', 'button-secondary', 'button-ghost'].includes(variant) },
    { [styles[`${textLinkClass}--icon-${modifiedIconPosition}`]]: modifiedIconPosition },
    { [styles[`${textLinkClass}--${size}`]]: size },
    className
  );

  return (
    <a
      href={href}
      target={target}
      className={classes}
      rel={rel}
      onClick={onClick}
      title={title}
      data-testid="text-link-component"
      aria-label={ariaLabel}
    >
      <span className={styles[`${textLinkClass}__text`]}>{children}</span>
      {chooseIcon()}
    </a>
  );
};

export const IconLink: React.FC<IconLinkProps> = ({
  href,
  target,
  rel,
  onClick,
  className,
  icon,
  variant = 'button-primary',
  size = 'medium',
  'aria-label': ariaLabel
}) => {
  const classes = classnames(
    styles[iconLinkClass],
    { [styles[`${iconLinkClass}--${variant}`]]: variant },
    { [styles[`${iconLinkClass}--button`]]: ['button-primary', 'button-secondary', 'button-ghost'].includes(variant) },
    { [styles[`${iconLinkClass}--${size}`]]: size },
    className
  );

  return (
    <a href={href} target={target} className={classes} rel={rel} onClick={onClick} aria-label={ariaLabel}>
      <Icon {...icon} className={styles[`${iconLinkClass}__icon`]} />
    </a>
  );
};

/**
 * The Link component is typed by a union of TextLinkProps or IconLinkProps. The props you give it determine if its a IconLink
 * or a TextLink component. While you can use TextLink or IconLink directly we would recommend to use Link.
 *
 * Not adding children will result in an IconLink. Using the icon prop makes the aria-label prop mandatory.
 * Using children will make the icon prop optional and will result in an TextLink.
 */

export const Link: React.FC<LinkProps> = (props: LinkProps) => {
  if (isTextLinkProps(props)) {
    return <TextLink {...props} />;
  }

  return <IconLink {...props} />;
};
